import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { addDoc, collection, orderBy, query } from 'firebase/firestore';
import { db, initAnalytics, VSCODE_EXTENSION_URL, watchAuthState } from './firebase';
import { subscribeWithRetry } from './firestoreRetry';
import { translate, getStoredLanguage, setStoredLanguage } from './i18n';
import { getStoredTheme, useTheme } from './theme';
import LanguageToggle from './LanguageToggle';
import EyeBadge from './EyeBadge';
import './SubscribePage.css';
import './WorkspacesPage.css';
import './RemotePage.css';

// A VS Code window refreshes its heartbeat every 60s (see vscode-extension/
// src/remote.ts's HEARTBEAT_MS) - older than ~2.5 of those means it's gone.
const ONLINE_WINDOW_MS = 150000;
const MAX_REMOTE_MESSAGE = 1500;
// Must match vscode-extension/src/remoteSecurity.ts's PIN_ITERATIONS.
const PIN_ITERATIONS = 150000;

// Proves the user knows the PIN set in VS Code without ever sending it:
// PBKDF2(pin, `${proposalId}:${nonce}`), recomputed and checked by the
// extension (RemoteSecurity.verifyProof). Salting with the proposal id and
// a one-time nonce means a proof can't be reused for anything else.
async function pinProof(pin, proposalId) {
  const nonceBytes = crypto.getRandomValues(new Uint8Array(16));
  const nonce = Array.from(nonceBytes, (b) => b.toString(16).padStart(2, '0')).join('');
  const enc = new TextEncoder();
  const key = await crypto.subtle.importKey('raw', enc.encode(pin), 'PBKDF2', false, ['deriveBits']);
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', salt: enc.encode(`${proposalId}:${nonce}`), iterations: PIN_ITERATIONS, hash: 'SHA-256' },
    key,
    256
  );
  const proof = Array.from(new Uint8Array(bits), (b) => b.toString(16).padStart(2, '0')).join('');
  return { nonce, proof };
}

// Splits a reply into prose and ``` code blocks, rendered as plain text -
// React escapes everything, so nothing from the model is ever parsed as HTML.
function ReplyText({ text }) {
  const parts = text.split('```');
  return parts.map((part, i) => {
    if (i % 2 === 1) {
      const nl = part.indexOf('\n');
      return <pre key={i} className="remote-code"><code>{nl >= 0 ? part.slice(nl + 1) : part}</code></pre>;
    }
    return part.trim() ? <p key={i} className="remote-text">{part.trim()}</p> : null;
  });
}

// Proposed code changes from the extension (see vscode-extension/src/
// codeEdits.ts): shown as a before/after preview with Aplicar/Descartar.
// Nothing is written on the computer until Aplicar is tapped.
const EDIT_LABEL_KEYS = {
  edit: 'remoteEditModify',
  rewrite: 'remoteEditRewrite',
  create: 'remoteEditCreate',
  delete: 'remoteEditDelete'
};

function EditProposal({ message, onDecide, t, disabled, device, onSetupPin }) {
  const [askingPin, setAskingPin] = useState(false);
  const [pin, setPin] = useState('');
  const edits = Array.isArray(message.edits) ? message.edits : [];
  const commands = Array.isArray(message.commands) ? message.commands : [];
  const results = Array.isArray(message.commandResults) ? message.commandResults : [];
  const status = message.editStatus;
  return (
    <div className="remote-edits">
      {edits.map((edit, i) => (
        <details key={i} className="remote-edit" open={edits.length === 1}>
          <summary className={edit.action === 'delete' ? 'remote-edit-delete' : undefined}>
            {t(EDIT_LABEL_KEYS[edit.action] || 'remoteEditModify')} <code>{edit.path}</code>
          </summary>
          {edit.action === 'delete' ? (
            <p className="remote-edit-status error">{t('remoteEditDeleteNote')}</p>
          ) : edit.action === 'create' || edit.action === 'rewrite' ? (
            <pre className="remote-code remote-diff-add"><code>{edit.content}</code></pre>
          ) : (
            <>
              <pre className="remote-code remote-diff-del"><code>{edit.search}</code></pre>
              <pre className="remote-code remote-diff-add"><code>{edit.replace}</code></pre>
            </>
          )}
        </details>
      ))}
      {commands.length > 0 && (
        <div className="remote-commands">
          <span className="remote-commands-label">{t('remoteCommandsLabel')}</span>
          {commands.map((command, i) => (
            <pre key={i} className="remote-code remote-command"><code>{`> ${command}`}</code></pre>
          ))}
        </div>
      )}
      {results.map((r, i) => (
        <details key={i} className="remote-edit" open={r.exitCode !== 0}>
          <summary className={r.exitCode === 0 ? undefined : 'remote-edit-delete'}>
            <code>{r.command}</code> · {r.exitCode === 0 ? t('remoteCommandOk') : `${t('remoteCommandFailed')} (${r.exitCode ?? '?'})`}
          </summary>
          <pre className="remote-code"><code>{r.output || t('remoteNoOutput')}</code></pre>
        </details>
      ))}
      {status === 'proposed' && device && !device.pinSet && (
        <p className="remote-edit-status error">
          {t('remoteNeedPin')}{' '}
          <button type="button" className="remote-link-btn" onClick={onSetupPin}>{t('remotePinLink')} →</button>
        </p>
      )}
      {status === 'proposed' && device?.locked && <p className="remote-edit-status error">{t('remoteLocked')}</p>}
      {status === 'proposed' && commands.length > 0 && device?.reviewPending && (
        <p className="remote-edit-status error">{t('remoteReviewPending')}</p>
      )}
      {status === 'proposed' && commands.length > 0 && edits.length > 0 && (
        <p className="remote-edit-status error">{t('remoteNoEditAndRun')}</p>
      )}
      {status === 'proposed' && commands.length > 0 && device && !device.allowCommands && (
        <p className="remote-edit-status error">{t('remoteCommandsOff')}</p>
      )}
      {status === 'proposed' && askingPin && (
        <form
          className="remote-pin-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!/^\d{6,12}$/.test(pin)) return;
            onDecide(message, 'apply', pin);
            setPin('');
            setAskingPin(false);
          }}
        >
          <input
            className="workspace-input"
            type="password"
            inputMode="numeric"
            autoComplete="off"
            placeholder={t('remotePinPlaceholder')}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 12))}
            autoFocus
          />
          <button type="submit" className="workspace-submit-btn" disabled={disabled || pin.length < 6}>
            {t('remoteConfirm')}
          </button>
        </form>
      )}
      {status === 'proposed' && !askingPin && (
        <div className="remote-edit-actions">
          <button type="button" className="workspace-submit-btn" disabled={disabled} onClick={() => setAskingPin(true)}>
            {commands.length ? t('remoteApplyAndRun') : t('remoteApply')}
          </button>
          <button type="button" className="workspace-submit-btn secondary" disabled={disabled} onClick={() => onDecide(message, 'reject')}>
            {t('remoteDiscard')}
          </button>
        </div>
      )}
      {status === 'applying' && <span className="remote-edit-status">{t('remoteApplying')}</span>}
      {status === 'applied' && <span className="remote-edit-status ok">{t('remoteApplied')}</span>}
      {status === 'rejected' && <span className="remote-edit-status">{t('remoteRejected')}</span>}
      {status === 'failed' && (
        <span className="remote-edit-status error">{t('remoteApplyFailed')}{message.editError ? `: ${message.editError}` : ''}</span>
      )}
    </div>
  );
}

/**
 * VS Code remoto: message one of your open VS Code windows from the phone.
 * The extension (vscode-extension/src/remote.ts) registers each window
 * under /remote/{uid}/devices, picks up 'pending' messages written here,
 * answers them with what's open in the editor as context, and writes the
 * reply back into the same subcollection - which this page just listens to.
 */
export default function RemotePage() {
  const navigate = useNavigate();
  const setupPin = () => navigate('/', { state: { settings: 'pin' } });
  const [language, setLanguage] = useState(() => getStoredLanguage());
  const t = (key) => translate(language, key);
  useTheme(getStoredTheme());

  const [user, setUser] = useState(null);
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [now, setNow] = useState(Date.now());
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const coarsePointer = useMemo(() => typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches, []);
  // The message box grows with what's typed, up to about six lines.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [draft]);

  useEffect(() => {
    initAnalytics();
  }, []);

  useEffect(() => {
    const unsubscribe = watchAuthState((nextUser) => {
      setUser(nextUser);
      if (!nextUser) navigate('/');
    });
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-evaluates who's online as heartbeats age, without new snapshots.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!user?.uid) return;
    return subscribeWithRetry(
      () => collection(db, 'remote', user.uid, 'devices'),
      (snapshot) => setDevices(snapshot.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => setError(t('remoteErrLoad'))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid || !deviceId) {
      setMessages([]);
      return;
    }
    return subscribeWithRetry(
      () => query(collection(db, 'remote', user.uid, 'devices', deviceId, 'messages'), orderBy('createdAt', 'asc')),
      (snapshot) => setMessages(snapshot.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => setError(t('remoteErrLoad'))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, deviceId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const isOnline = (device) => device.online !== false && now - (device.lastSeen || 0) < ONLINE_WINDOW_MS;
  const sortedDevices = useMemo(
    () => [...devices].sort((a, b) => (isOnline(b) - isOnline(a)) || (b.lastSeen || 0) - (a.lastSeen || 0)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [devices, now]
  );
  const device = devices.find((d) => d.id === deviceId) || null;
  const deviceOnline = device ? isOnline(device) : false;
  const waiting = messages.some((m) => m.role === 'user' && (m.status === 'pending' || m.status === 'processing'));

  const handleSend = async (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text || sending || waiting || !user || !deviceId) return;
    setSending(true);
    setError('');
    try {
      await addDoc(collection(db, 'remote', user.uid, 'devices', deviceId, 'messages'), {
        role: 'user',
        text: text.slice(0, MAX_REMOTE_MESSAGE),
        status: 'pending',
        createdAt: Date.now()
      });
      setDraft('');
    } catch {
      setError(t('remoteErrSend'));
    } finally {
      setSending(false);
    }
  };

  // Aplicar/Descartar travel as an 'action' message the extension picks up
  // on its next poll (see vscode-extension/src/remote.ts's handleAction).
  const [deciding, setDeciding] = useState(null);
  const handleDecide = async (message, action, pin) => {
    if (!user || !deviceId || deciding) return;
    setDeciding(message.id);
    setError('');
    try {
      const approval = action === 'apply' ? await pinProof(pin, message.id) : {};
      await addDoc(collection(db, 'remote', user.uid, 'devices', deviceId, 'messages'), {
        role: 'action',
        action,
        target: message.id,
        status: 'pending',
        createdAt: Date.now(),
        ...approval
      });
    } catch {
      setError(t('remoteErrSend'));
    } finally {
      setDeciding(null);
    }
  };
  const pendingActionFor = (id) => messages.some((m) => m.role === 'action' && m.target === id && m.status === 'pending');

  const statusLabel = (m) => {
    if (m.status === 'pending') return deviceOnline ? t('remoteStatusSent') : t('remoteStatusQueued');
    // 'processing' shows as the winking eye on VS Code's side instead.
    if (m.status === 'error') return `${t('remoteStatusError')}${m.error ? `: ${m.error}` : ''}`;
    return '';
  };

  return (
    <div className="sub-page">
      <header className="sub-page-header">
        <div className="sub-page-titlebar">
          <div className="sub-page-titlebar-start">
            <button
              className="sub-back-icon-btn"
              type="button"
              onClick={() => (deviceId ? setDeviceId(null) : navigate('/'))}
              aria-label={t('backAria')}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 20, height: 20 }}>
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="sub-page-title">{device ? device.name : t('remoteTitle')}</h1>
          </div>
          <LanguageToggle
            language={language}
            onChange={(lang) => {
              setLanguage(lang);
              setStoredLanguage(lang);
            }}
          />
        </div>
      </header>

      <main className="sub-page-main workspaces-main">
        <div className="workspaces-panel remote-panel">
          {error && <p className="workspace-error">{error}</p>}

          {!deviceId && (
            <>
              <p className="remote-intro">{t('remoteIntro')}</p>
              <p className="remote-intro remote-terms">
                {t('remoteTermsNote')}{' '}
                <a href="/terminos.html#coding-agent" target="_blank" rel="noreferrer">{t('remoteTermsLink')}</a>
              </p>
              <div className="workspace-list">
                {sortedDevices.length === 0 && (
                  <div className="workspace-empty">
                    <p>{t('remoteNoDevices')}</p>
                    <a href={VSCODE_EXTENSION_URL} target="_blank" rel="noreferrer">{t('vscodeExtensionLink')} →</a>
                  </div>
                )}
                {sortedDevices.map((d) => (
                  <button key={d.id} type="button" className="workspace-list-item" onClick={() => setDeviceId(d.id)}>
                    <span className="remote-device">
                      <span className="workspace-list-item-name">{d.name || 'VS Code'}</span>
                      {d.activeFile && <span className="remote-device-file">{d.activeFile}</span>}
                    </span>
                    <span className={`remote-dot${isOnline(d) ? ' online' : ''}`}>
                      {isOnline(d) ? t('remoteOnline') : t('remoteOffline')}
                    </span>
                  </button>
                ))}
              </div>
            </>
          )}

          {deviceId && (
            <>
              <p className={`remote-device-status${deviceOnline ? ' online' : ''}`}>
                {deviceOnline
                  ? `${t('remoteOnline')}${device?.activeFile ? ` · ${device.activeFile}` : ''}`
                  : t('remoteOfflineHint')}
                <button type="button" className="remote-link-btn" onClick={setupPin}>
                  {device?.pinSet ? t('pinChange') : t('remotePinLink')}
                </button>
              </p>
              <div className="remote-messages">
                {messages.length === 0 && <p className="workspace-empty">{t('remoteEmptyChat')}</p>}
                {messages.filter((m) => m.role !== 'action' && m.role !== 'pinRequest' && m.kind !== 'output').map((m) => (
                  <div key={m.id} className={`remote-msg ${m.role === 'ai' ? 'ai' : 'user'}${m.kind === 'question' ? ' question' : ''}`}>
                    {m.kind === 'question' && <span className="remote-question-label">{t('remoteQuestionLabel')}</span>}
                    {m.role === 'ai' ? <ReplyText text={m.text || ''} /> : <p className="remote-text">{m.text}</p>}
                    {m.kind === 'edits' && (
                      <EditProposal
                        message={pendingActionFor(m.id) ? { ...m, editStatus: 'applying' } : m}
                        onDecide={handleDecide}
                        disabled={!!deciding}
                        device={device}
                        onSetupPin={setupPin}
                        t={t}
                      />
                    )}
                    {m.role === 'user' && statusLabel(m) && (
                      <span className={`remote-status ${m.status}`}>{statusLabel(m)}</span>
                    )}
                  </div>
                ))}
                {messages.some((m) => m.role === 'user' && m.status === 'processing') && (
                  <div className="remote-msg ai remote-thinking">
                    <EyeBadge label={t('remoteStatusThinking')} size={24} />
                    <span>{t('remoteStatusThinking')}</span>
                  </div>
                )}
                <div ref={endRef} />
              </div>
              <form className="remote-composer" onSubmit={handleSend}>
                <textarea
                  ref={inputRef}
                  className="remote-composer-input"
                  placeholder={t('remotePlaceholder')}
                  aria-label={t('remotePlaceholder')}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    // Enter sends on a keyboard; on a phone it's a new line
                    // and the button sends.
                    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing && !coarsePointer) handleSend(e);
                  }}
                  maxLength={MAX_REMOTE_MESSAGE}
                  rows={1}
                  disabled={sending}
                />
                <button
                  className="remote-composer-send"
                  type="submit"
                  disabled={sending || !draft.trim() || waiting}
                  aria-label={t('remoteSend')}
                  title={t('remoteSend')}
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M12 19V5M5.5 11.5 12 5l6.5 6.5" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                {draft.length > MAX_REMOTE_MESSAGE * 0.8 && (
                  <span className="remote-composer-count">{draft.length}/{MAX_REMOTE_MESSAGE}</span>
                )}
              </form>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
